import React, { useState } from 'react';
import useGameStore from '../store/gameStore';
import { computeReseauEffects } from '../utils/magasin';
import { computeAmelFx } from '../utils/ameliorations';
import { getSecteur } from '../utils/retail';
import { formatMoney } from '../utils/format';
import { Store, Map } from 'lucide-react';
import TunnelOuverture from '../components/ouverture/TunnelOuverture';
import { getLocalisation, getTypeMagasin } from '../utils/ouverture';
import { VisuelMagasin } from '../components/game/VisuelMagasin';
import { EvolutionMagasin } from '../components/game/EvolutionMagasin';
import { ComparateurMagasins } from '../components/game/ComparateurMagasins';
import { toast } from '../components/ui/sonner';

const CONCURRENCE_BADGE = {
  faible: 'bg-emerald-600/20 text-emerald-400',
  moyenne: 'bg-amber-600/20 text-amber-400',
  forte: 'bg-red-600/20 text-red-400',
};

export default function Magasin() {
  const retail = useGameStore(s => s.retail);
  const company = useGameStore(s => s.company);
  const treasury = useGameStore(s => s.treasury);
  const totalDays = useGameStore(s => s.totalDays) || 0;
  const ouvrirMagasinTunnel = useGameStore(s => s.ouvrirMagasinTunnel);
  const [tunnelOuvert, setTunnelOuvert] = useState(false);

  if (!retail) return <div className="text-slate-400 text-sm" data-testid="magasin-empty">Aucun magasin actif.</div>;
  const sec = getSecteur(retail.secteur);
  const amelFx = computeAmelFx(retail.magasin);
  const reseau = computeReseauEffects(retail, totalDays);

  return (
    <div className="space-y-5" data-testid="magasin-page">
      <div>
        <p className="overline">{sec?.nom} · {retail.magasin?.surface} m² · entretien {formatMoney(Math.round(amelFx.entretienJour * 30))}/mois</p>
        <h1 className="font-display text-2xl md:text-3xl font-black tracking-tight text-foreground mt-0.5 flex items-center gap-2">
          <Store className="w-6 h-6 text-accent" /> Mon magasin & mon réseau
        </h1>
      </div>

      {/* SCD R2 — Visuel photoréaliste du magasin principal */}
      {retail.magasin?.typeId && retail.magasin?.localisationId && (
        <VisuelMagasin
          localisationId={retail.magasin.localisationId}
          typeId={retail.magasin.typeId}
          secteurId={retail.secteur}
          positionnement={retail.positionnement}
          className="w-full max-h-72 border border-ink-600 shadow-lg"
          testId="visuel-magasin-principal"
        />
      )}

      {/* SCD R3 — Évolution du magasin : arbre d'améliorations par magasin */}
      <EvolutionMagasin />

      {/* SCD R3 — Comparateur des points de vente */}
      <ComparateurMagasins />

      {/* SCD R1 — Réseau de magasins + tunnel d'ouverture */}
      <div className="surface-card p-4" data-testid="reseau-magasins">
        <h3 className="font-display text-sm font-bold text-foreground tracking-tight uppercase mb-1 flex items-center gap-2"><Map className="w-4 h-4 text-accent" /> Réseau de magasins</h3>
        <p className="text-[11px] text-slate-500 mb-3">
          Le réseau partage assortiment, prix, équipe et marketing : chaque magasin ajoute sa contribution au trafic global
          (contribution actuelle : <b className="font-mono text-slate-200">×{reseau.demandeMult.toFixed(2)}</b>, coûts {formatMoney(Math.round(reseau.coutJour * 30))}/mois).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
          {[{ ...retail.magasin, principal: true }, ...(retail.magasins || [])].map((m, i) => {
            const loc = getLocalisation(m.localisationId);
            const type = getTypeMagasin(m.typeId);
            return (
              <div key={m.id || 'principal'} className="rounded-lg border border-accent/40 bg-accent/5 p-3" data-testid={`magasin-reseau-${i}`}>
                {m.typeId && m.localisationId && (
                  <VisuelMagasin
                    localisationId={m.localisationId}
                    typeId={m.typeId}
                    secteurId={m.secteurId || retail.secteur}
                    positionnement={m.positionnement || retail.positionnement}
                    className="w-full h-28 mb-2 border border-ink-700"
                    testId={`visuel-reseau-${i}`}
                  />
                )}
                <p className="font-bold text-sm text-slate-100">{type?.emoji || '🏪'} {m.nom || m.nomType || 'Magasin principal'} {m.principal && <span className="text-[9px] uppercase text-accent">· principal</span>}</p>
                <p className="text-[11px] font-mono text-slate-400 mt-1">
                  {m.surface} m² · loyer {formatMoney(m.loyerMensuel)}/mois{loc ? ` · ${loc.emoji} ${loc.nom}` : ''}
                </p>
                {loc && <p className="text-[10px] text-slate-500 mt-0.5">trafic ×{loc.coef_trafic} · panier ×{loc.coef_panier} · concurrence {loc.concurrence}</p>}
              </div>
            );
          })}
        </div>
        {!tunnelOuvert ? (
          <button
            type="button"
            onClick={() => setTunnelOuvert(true)}
            className="w-full bg-accent text-white px-3 py-2.5 rounded text-sm font-bold hover:bg-accent-hover transition-colors"
            data-testid="ouvrir-nouveau-magasin"
          >
            + Ouvrir un nouveau magasin (tunnel en 5 étapes)
          </button>
        ) : (
          <div className="rounded-lg border border-ink-600 bg-ink-900/40 p-4">
            <TunnelOuverture
              mode="expansion"
              secteurId={retail.secteur}
              secteurConfigs={retail.secteurConfigs || { [retail.secteur]: { positionnement: retail.positionnement, localisationId: retail.magasin?.localisationId || null } }}
              treasury={treasury}
              onCancel={() => setTunnelOuvert(false)}
              onComplete={(cfg) => {
                const res = ouvrirMagasinTunnel(cfg);
                if (res?.ok) {
                  toast('Nouveau magasin ouvert !', { description: `${res.magasin.nom} — ${formatMoney(res.cout)} ${cfg.financement === 'emprunt' ? 'financés par emprunt (48 mois)' : 'débités de la trésorerie'}.` });
                  setTunnelOuvert(false);
                } else if (res?.reason === 'fonds') {
                  toast.error('Trésorerie insuffisante — choisissez le financement par emprunt.');
                }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
